import { type ReactNode, useEffect, useRef } from "react";
import { DragIcon } from "vega-icons";

import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import {
  draggable,
  dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { ThemedIconButton } from "@vega-core/components/Buttons/ThemedIconButton";
import { Badge } from "@vega-core/components/shadcn/badge";
import { DropdownMenuCheckboxItem } from "@vega-core/components/shadcn/dropdown-menu";
import { Tooltip } from "@vega-core/components/Tooltip/Tooltip";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

export const COLUMN_ORDER_ROW_EVENT_TYPE = "vega-column-order-row" as const;

export type ColumnOrderDragData = {
  columnId: string;
  eventType: typeof COLUMN_ORDER_ROW_EVENT_TYPE;
};

export const isColumnOrderDragData = (
  data: Record<string, unknown>,
): data is ColumnOrderDragData =>
  data.eventType === COLUMN_ORDER_ROW_EVENT_TYPE && "columnId" in data;

type ColumnOrderRowProps = {
  id: string;
  label: string;
  checked: boolean;
  checkedIcon?: ReactNode;
  onCheckedChange: () => void;
  trailing?: ReactNode;
  disabled?: boolean;
  disabledTooltip?: ReactNode;
};

export const ColumnOrderRow = ({
  id,
  label,
  checked,
  checkedIcon,
  onCheckedChange,
  trailing,
  disabled = false,
  disabledTooltip,
}: ColumnOrderRowProps) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLSpanElement>(null);
  const {
    value: isDragging,
    setTrue: startDragging,
    setFalse: endDragging,
  } = useBoolean(false);
  const {
    value: isOver,
    setTrue: startOver,
    setFalse: endOver,
  } = useBoolean(false);

  useEffect(() => {
    const rowElement = rowRef.current;
    const dragHandleElement = dragHandleRef.current;

    if (!rowElement || !dragHandleElement) return;

    return combine(
      draggable({
        element: rowElement,
        dragHandle: dragHandleElement,
        getInitialData: (): ColumnOrderDragData => ({
          columnId: id,
          eventType: COLUMN_ORDER_ROW_EVENT_TYPE,
        }),
        onDragStart: startDragging,
        onDrop: endDragging,
      }),
      dropTargetForElements({
        element: rowElement,
        getData: (): ColumnOrderDragData => ({
          columnId: id,
          eventType: COLUMN_ORDER_ROW_EVENT_TYPE,
        }),
        canDrop: ({ source }) =>
          isColumnOrderDragData(source.data) && source.data.columnId !== id,
        onDragEnter: startOver,
        onDragLeave: endOver,
        onDrop: endOver,
      }),
    );
  }, [id, startDragging, endDragging, startOver, endOver]);

  return (
    <div
      className={cnTwMerge(
        "flex items-center gap-1 rounded-sm",
        isDragging && "opacity-60",
        isOver && !isDragging && "bg-background/tertiary/40",
      )}
      data-testid={`column-order-row-${id}`}
      ref={rowRef}
    >
      <span className="shrink-0" ref={dragHandleRef}>
        <ThemedIconButton
          ariaLabel={m.dataGrid_dragToReorder()}
          className="cursor-grab active:cursor-grabbing"
          dataTestId={`column-order-drag-handle-${id}`}
          icon={<DragIcon className="size-3.5" />}
          size="extraSmall"
          variant="noBackground"
        />
      </span>
      <Tooltip content={disabled ? disabledTooltip : undefined}>
        <span className="flex flex-1">
          <DropdownMenuCheckboxItem
            checked={checked}
            checkedIcon={checkedIcon}
            className="flex-1"
            data-testid={`column-order-checkbox-${id}`}
            disabled={disabled}
            onCheckedChange={onCheckedChange}
            onSelect={(event) => event.preventDefault()}
          >
            <span className="flex-1 truncate">{label}</span>
            {disabled ? (
              <Badge
                className="ml-2 shrink-0 rounded-small border-transparent bg-background/component/10 px-1.5 py-0.5 body-extra-small-medium text-text/secondary"
                variant="outline"
              >
                {m.dataGrid_filteredColumnTag()}
              </Badge>
            ) : null}
            {trailing}
          </DropdownMenuCheckboxItem>
        </span>
      </Tooltip>
    </div>
  );
};
